import React, { useState, useEffect, useCallback } from 'react';
import OAuthConnectButton from './OAuthConnectButton';

// ---------- Types ----------

interface GA4Property {
  propertyId: string;
  displayName: string;
}

interface ScorecardData {
  label: string;
  value: number;
  previousValue?: number;
  format: 'number' | 'percent' | 'duration';
}

interface ChannelRow {
  channel: string;
  sessions: number;
  users: number;
  bounceRate: number;
  avgSessionDuration: number;
}

interface LandingPageRow {
  page: string;
  sessions: number;
  users: number;
  bounceRate: number;
}

interface DeviceSplit {
  device: string;
  sessions: number;
}

interface CityRow {
  city: string;
  sessions: number;
  users: number;
}

interface TrafficRow {
  date: string;
  sessions: number;
  users: number;
}

interface GAReport {
  scorecards: ScorecardData[];
  trafficOverTime: TrafficRow[];
  channels: ChannelRow[];
  landingPages: LandingPageRow[];
  devices: DeviceSplit[];
  topCities: CityRow[];
}

type DateRange = '30d' | '90d' | '6m' | '12m';

// ---------- Helpers ----------

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchJSON<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...opts, headers: { ...authHeaders(), ...opts?.headers } });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed (${res.status})`);
  }
  return res.json();
}

function fmtNumber(n: number | undefined | null): string {
  if (n == null || isNaN(n)) return '-';
  return n.toLocaleString('en-US');
}

function fmtPercent(n: number | undefined | null): string {
  if (n == null || isNaN(n)) return '-';
  return `${n.toFixed(1)}%`;
}

function fmtDuration(seconds: number | undefined | null): string {
  if (seconds == null || isNaN(seconds)) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function formatValue(value: number, format: 'number' | 'percent' | 'duration'): string {
  if (format === 'percent') return fmtPercent(value);
  if (format === 'duration') return fmtDuration(value);
  return fmtNumber(value);
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

// For bounce rate / duration, "down" can be good — we track which are inverse
const INVERSE_METRICS = new Set(['Bounce Rate']);

// ---------- Sub-components ----------

function Scorecard({ card, compare }: { card: ScorecardData; compare: boolean }) {
  const change =
    compare && card.previousValue != null ? pctChange(card.value, card.previousValue) : null;
  const isInverse = INVERSE_METRICS.has(card.label);
  const isPositive = change != null ? (isInverse ? change < 0 : change > 0) : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-1 min-w-[140px]">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {card.label}
      </span>
      <span className="text-2xl font-bold text-gray-900">{formatValue(card.value, card.format)}</span>
      {change != null && (
        <span
          className={`text-xs font-medium ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {change > 0 ? '+' : ''}
          {change.toFixed(1)}% vs prev
        </span>
      )}
    </div>
  );
}

function CSSBarChart({ rows, maxVal }: { rows: { label: string; value: number }[]; maxVal: number }) {
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = maxVal > 0 ? (r.value / maxVal) * 100 : 0;
        return (
          <div key={r.label} className="flex items-center gap-3">
            <span className="text-sm text-gray-700 w-24 truncate" title={r.label}>
              {r.label}
            </span>
            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
              <div
                className="bg-blue-500 h-full rounded-full transition-all"
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-600 w-20 text-right">
              {fmtNumber(r.value)} ({pct.toFixed(1)}%)
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DataTable({
  columns,
  rows,
}: {
  columns: { key: string; label: string; format?: 'number' | 'percent' | 'duration' }[];
  rows: Record<string, any>[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-4 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
              {columns.map((col) => {
                const val = row[col.key];
                let display: string;
                if (typeof val === 'number') {
                  display = formatValue(val, col.format || 'number');
                } else {
                  display = String(val ?? '-');
                }
                return (
                  <td key={col.key} className="px-4 py-2 text-gray-700 whitespace-nowrap">
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-400">
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Main Component ----------

const GoogleAnalytics: React.FC = () => {
  const [connected, setConnected] = useState(false);
  const [properties, setProperties] = useState<GA4Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [compare, setCompare] = useState(false);
  const [report, setReport] = useState<GAReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [propsLoading, setPropsLoading] = useState(false);
  const [error, setError] = useState('');

  // Load properties when connected
  useEffect(() => {
    if (!connected) {
      setProperties([]);
      setSelectedProperty('');
      setReport(null);
      return;
    }
    let cancelled = false;
    setPropsLoading(true);
    fetchJSON<{ properties: GA4Property[] }>('/api/seotools/google-analytics/properties')
      .then((resp) => {
        if (cancelled) return;
        const data = resp.properties || [];
        setProperties(data);
        if (data.length > 0) setSelectedProperty(data[0].propertyId);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setPropsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  // Fetch report when property / dateRange / compare changes
  const fetchReport = useCallback(async () => {
    if (!selectedProperty) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchJSON<GAReport>('/api/seotools/google-analytics/report', {
        method: 'POST',
        body: JSON.stringify({ propertyId: selectedProperty, dateRange, compare }),
      });
      setReport(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, dateRange, compare]);

  useEffect(() => {
    if (connected && selectedProperty) {
      fetchReport();
    }
  }, [connected, selectedProperty, dateRange, compare, fetchReport]);

  // ---------- Render ----------

  const dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '6m', label: 'Last 6 months' },
    { value: '12m', label: 'Last 12 months' },
  ];

  const deviceTotal = report?.devices.reduce((s, d) => s + d.sessions, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Google Analytics</h2>
          <p className="text-sm text-gray-500 mt-1">GA4 reporting dashboard</p>
        </div>
        <OAuthConnectButton
          service="ga4"
          label="Google Analytics"
          onConnect={() => setConnected(true)}
          onDisconnect={() => setConnected(false)}
        />
      </div>

      {/* Not connected state */}
      {!connected && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-blue-400 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-blue-800">Connect Google Analytics</h3>
          <p className="text-sm text-blue-600 mt-1">
            Link your GA4 account to view traffic reports, audience data, and more.
          </p>
        </div>
      )}

      {/* Controls — shown when connected */}
      {connected && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-center gap-4">
          {/* Property selector */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Property</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              disabled={propsLoading}
            >
              {propsLoading && <option>Loading...</option>}
              {!propsLoading && properties.length === 0 && <option>No properties found</option>}
              {properties.map((p) => (
                <option key={p.propertyId} value={p.propertyId}>
                  {p.displayName} ({p.propertyId})
                </option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Date Range</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
            >
              {dateRangeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Compare toggle */}
          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              role="switch"
              aria-checked={compare}
              onClick={() => setCompare(!compare)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                compare ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  compare ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700">Compare to previous period</span>
          </div>

          {/* Loading indicator */}
          {loading && (
            <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Loading report...
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Report */}
      {report && !loading && (
        <div className="space-y-6">
          {/* Scorecards */}
          <div className="flex flex-wrap gap-3">
            {report.scorecards.map((card) => (
              <Scorecard key={card.label} card={card} compare={compare} />
            ))}
          </div>

          {/* Traffic Over Time */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Traffic Over Time</h3>
            {report.trafficOverTime.length > 0 ? (
              <>
                {/* CSS bar chart for sessions */}
                <div className="space-y-1 mb-4">
                  {report.trafficOverTime.map((row) => {
                    const maxSessions = Math.max(
                      ...report.trafficOverTime.map((r) => r.sessions),
                      1
                    );
                    const pct = (row.sessions / maxSessions) * 100;
                    return (
                      <div key={row.date} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 w-20 shrink-0">{row.date}</span>
                        <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                          <div
                            className="bg-blue-500 h-full rounded transition-all"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                        <span className="text-gray-600 w-16 text-right font-medium">
                          {fmtNumber(row.sessions)}
                        </span>
                        <span className="text-gray-400 w-16 text-right">
                          {fmtNumber(row.users)} users
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-gray-400 text-sm">No traffic data for this period.</p>
            )}
          </div>

          {/* Channels */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Channels</h3>
            <DataTable
              columns={[
                { key: 'channel', label: 'Channel' },
                { key: 'sessions', label: 'Sessions', format: 'number' },
                { key: 'users', label: 'Users', format: 'number' },
                { key: 'bounceRate', label: 'Bounce Rate', format: 'percent' },
                { key: 'avgSessionDuration', label: 'Avg Duration', format: 'duration' },
              ]}
              rows={report.channels}
            />
          </div>

          {/* Landing Pages */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Landing Pages</h3>
            <DataTable
              columns={[
                { key: 'page', label: 'Page' },
                { key: 'sessions', label: 'Sessions', format: 'number' },
                { key: 'users', label: 'Users', format: 'number' },
                { key: 'bounceRate', label: 'Bounce Rate', format: 'percent' },
              ]}
              rows={report.landingPages}
            />
          </div>

          {/* Devices */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Devices</h3>
            <CSSBarChart
              rows={report.devices.map((d) => ({ label: d.device, value: d.sessions }))}
              maxVal={deviceTotal}
            />
          </div>

          {/* Top Cities */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Top Cities</h3>
            <DataTable
              columns={[
                { key: 'city', label: 'City' },
                { key: 'sessions', label: 'Sessions', format: 'number' },
                { key: 'users', label: 'Users', format: 'number' },
              ]}
              rows={report.topCities}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleAnalytics;
